import { useQueryClient } from '@tanstack/react-query';
import { insert, remove } from 'ramda';
import { useState, useEffect, useReducer, useCallback, useMemo } from 'react';

import { updateCategory, updateOffer, updateOption, updateOptionBinding } from 'modules/shared-menu/api';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { buildGetSelectionAllQueryKey } from 'modules/shared-menu/hooks/useSelectionData';

import { UpdateOptionPayload } from '../../../pages/SharedMenuEditorPage/components/OptionForm';
import { FullSelection, Offer, Option } from '../../../pages/SharedMenuEditorPage/types';
import {
  buildOptionValueTranslation,
  buildUpdateCategoryTranslationPayload,
  buildUpdateOfferTranslationPayload,
  buildUpdateOptionBindingTranslationPayload,
  buildUpdateOptionTranslationPayload,
} from '../transformers';
import { TranslationSectionItem } from '../types';
import { buildSectionItemKey, findRootElement } from '../utils';

type TranslationPayload = {
  value: string;
  item: TranslationSectionItem;
  pos: 'right' | 'left';
};

type TaskIdentifier = {
  key: string;
  pos: 'right' | 'left';
};

type Action =
  | {
      type: 'add';
      payload: TranslationPayload;
    }
  | { type: 'next' };

const payloadsReducer = (payloads: TranslationPayload[], action: Action) => {
  switch (action.type) {
    case 'add':
      return [...payloads, action.payload];

    case 'next':
      const [, ...next] = payloads;
      return next;

    default:
      return payloads;
  }
};

const useTranslationQueue = (args: {
  selectionData: FullSelection | undefined;
  rightLanguage: Language | null;
  leftLanguage: Language | null;
}) => {
  const { selectionData, rightLanguage, leftLanguage } = args;

  const queryClient = useQueryClient();

  const [payloads, dispatch] = useReducer(payloadsReducer, []);

  const [isExecutingTask, setIsExecutingTask] = useState(false);
  const [erroredTasks, setErroredTasks] = useState<TaskIdentifier[]>([]);
  const { vlmeConfig } = useSelectionInfoContext();

  const pendingTasks = useMemo<TaskIdentifier[]>(
    () => payloads.map(({ item, pos }) => ({ key: buildSectionItemKey(item), pos })),
    [payloads],
  );

  const translateItem = useCallback(
    async (payload: TranslationPayload) => {
      if (!selectionData) {
        throw new Error('No menu data available, this should not happen!');
      }
      const { value, item, pos } = payload;
      const { selection, offers, options } = selectionData;
      const { itemType, itemId, parentId, field } = item;
      const rootElement = findRootElement(itemType, itemId, parentId, selection, offers, options);

      if (!rootElement) {
        throw new Error('Described element not found in menu');
      }

      const language = pos === 'right' ? rightLanguage : leftLanguage;

      if (!language) {
        throw new Error('No language selected for column, this should not happen! ');
      }

      switch (itemType) {
        case 'category': {
          const targetCategoryId = itemId;

          const targetCategory = selection.categories.find(category => category.id === targetCategoryId);
          const isTranslatableField = field === 'name' || field === 'description';
          if (!targetCategory || !isTranslatableField) {
            return { selection, offers, options };
          }

          const updateCategoryPayload = buildUpdateCategoryTranslationPayload(
            field,
            value,
            language,
            selection.primary_language,
            targetCategory,
          );

          const updatedSelection = await updateCategory(selection.id, targetCategoryId, updateCategoryPayload);

          return { selection: updatedSelection, offers, options };
        }
        case 'item': {
          const item = rootElement as Offer;
          const updateOfferPayload = buildUpdateOfferTranslationPayload(
            field,
            value,
            language,
            selection.primary_language,
            item,
          );
          const updatedOffer = await updateOffer(selection.id, item.id, updateOfferPayload);
          const itemIndex = offers.findIndex(item => item.id === itemId);
          return { selection, offers: insert(itemIndex, updatedOffer, remove(itemIndex, 1, offers)), options };
        }
        case 'item_option': {
          const offerIndex = offers.findIndex(offer => offer.id === parentId);
          const offer = offers.find(offer => offer.id === parentId);
          const optionBinding = (offers[offerIndex].option_bindings ?? []).find(ob => ob.id === itemId);
          const isTranslatableField = field === 'name';
          if (!isTranslatableField || !optionBinding || !offer) {
            return { selection, offers, options };
          }

          const updateOptionBindingPayload = buildUpdateOptionBindingTranslationPayload(
            field,
            value,
            language,
            selection.primary_language,
            optionBinding,
          );

          const updatedOffer = await updateOptionBinding(
            selection.id,
            offer.id,
            optionBinding.id,
            updateOptionBindingPayload,
          );
          return { selection, offers: insert(offerIndex, updatedOffer, remove(offerIndex, 1, offers)), options };
        }
        case 'option': {
          const option = rootElement as Option;
          const isTranslatableField = field === 'name';
          if (!isTranslatableField) {
            return { selection, offers, options };
          }
          const updateOptionPayload = buildUpdateOptionTranslationPayload(
            field,
            value,
            language,
            selection.primary_language,
            option,
          );

          const updatedOption = await updateOption(selection.id, option.id, updateOptionPayload);

          const optionIndex = options.findIndex(item => item.id === itemId);
          return { selection, offers, options: insert(optionIndex, updatedOption, remove(optionIndex, 1, options)) };
        }
        case 'option_value': {
          const option = rootElement as Option;

          const targetOptionValueId = itemId;

          const targetOptionValue = option.values.find(value => value.id === targetOptionValueId);
          const isTranslatableField =
            field === 'name' ||
            field === 'ingredients' ||
            field === 'additives' ||
            field === 'nutrition_facts' ||
            field === 'allergens' ||
            field === 'producer_information' ||
            field === 'distributor_information';

          if (!targetOptionValue || !isTranslatableField) {
            return { selection, offers, options };
          }

          const updatedOptionValue = buildOptionValueTranslation(
            field,
            value,
            language,
            selection.primary_language,
            targetOptionValue,
          );

          const optionValueIndex = option.values.findIndex(value => value.id === targetOptionValueId);

          const updatedOptionValuesArray = insert(
            optionValueIndex,
            updatedOptionValue,
            remove(optionValueIndex, 1, option.values),
          );

          const { scope, id, version, ...payloadShapedOption } = option;

          const updateOptionPayload: UpdateOptionPayload = {
            ...payloadShapedOption,
            values: updatedOptionValuesArray,
          };

          const updatedOption = await updateOption(selection.id, option.id, updateOptionPayload);

          const optionIndex = options.findIndex(option => !!option.values.some(choice => choice.id === itemId));

          return { selection, offers, options: insert(optionIndex, updatedOption, remove(optionIndex, 1, options)) };
        }
        default:
          throw new Error(`Unknown type \`${itemType}\``);
      }
    },
    [leftLanguage, selectionData, rightLanguage],
  );

  useEffect(() => {
    const func = async () => {
      if (payloads.length > 0 && !isExecutingTask) {
        setIsExecutingTask(true);
        const [payload] = payloads;
        const key = buildSectionItemKey(payload.item);
        try {
          const updatedSelectionData = await translateItem(payload);
          queryClient.setQueryData(
            buildGetSelectionAllQueryKey(updatedSelectionData.selection.id, !vlmeConfig),
            updatedSelectionData,
          );
          setErroredTasks(prev => prev.filter(task => task.key !== key || task.pos !== payload.pos));
        } catch (error) {
          setErroredTasks(prev => [...prev, { key, pos: payload.pos }]);
        } finally {
          dispatch({ type: 'next' });
          setIsExecutingTask(false);
        }
      }
    };

    func();
  }, [payloads, isExecutingTask, translateItem, queryClient, vlmeConfig]);

  const addTranslationRequest = async (payload: TranslationPayload) => {
    dispatch({ type: 'add', payload });
  };

  return { addTranslationRequest, pendingTasks, erroredTasks };
};

export default useTranslationQueue;
